import {
  QBankStructureStatus,
  QuestionStatus,
  useExportQuestionItemAnalysisYearlyRecordsLazyQuery,
  useGetAllQBankStructuresQuery,
} from '@report/graphql';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Flex,
  Text,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { dayjs, triggerFileDownload } from '@shared/utils';

import { useItemAnalysisYearlySearchParams } from '../../hooks';
import { monthIndexMap } from '../item-analysis-yearly-table';

const MAXIMUM_EXPORT_COUNT = 50000;

interface ItemAnalysisYearlyReportExportModalProps {
  open: boolean;
  onClose: () => void;
  totalCount: number;
}

const QuestionStatusLabelMapping: Record<QuestionStatus, string> = {
  [QuestionStatus.Published]: 'Published',
  [QuestionStatus.Disabled]: 'Disabled',
};

export default function ItemAnalysisYearlyReportExportModal(
  props: Readonly<ItemAnalysisYearlyReportExportModalProps>,
) {
  const { open, onClose, totalCount } = props;
  const { showSnackbar } = useSnackbar();

  const [
    {
      startMonth,
      endMonth,
      startYear,
      endYear,
      examCourse,
      questionStatus,
      minExaminees,
      knowledgeGroupSelection,
      knowledgeGroupCustomThreshold,
      nonKnowledgeGroupSelection,
      nonKnowledgeGroupCustomThreshold,
    },
  ] = useItemAnalysisYearlySearchParams();

  const formattedStartDate =
    startMonth && startYear ? dayjs(`${startYear}-${startMonth}-01`).format('MMM YYYY') : '-';
  const formattedEndDate =
    endMonth && endYear ? dayjs(`${endYear}-${endMonth}-01`).format('MMM YYYY') : '-';

  const [exportQuestionItemAnalysisYearlyRecords, { loading: isExporting }] =
    useExportQuestionItemAnalysisYearlyRecordsLazyQuery();

  const { data: qBankStructuresData } = useGetAllQBankStructuresQuery({
    variables: {
      getAllQBankStructuresInput: {
        statuses: [
          QBankStructureStatus.Published,
          QBankStructureStatus.Unpublished,
          QBankStructureStatus.Archived,
        ],
      },
    },
  });

  const selectedQBankStructure =
    qBankStructuresData?.reportGetAllQBankStructures?.qBankStructures.find((structure) =>
      examCourse?.includes(structure.id),
    );
  const handleExport = async () => {
    await exportQuestionItemAnalysisYearlyRecords({
      variables: {
        exportQuestionItemAnalysisYearlyRecordsInput: {
          startedAt: new Date(
            startYear && startMonth
              ? Date.UTC(startYear, monthIndexMap[startMonth] - 1, 0, 17, 0, 0)
              : '',
          ),
          questionStatus: questionStatus,
          qBankStructureId: examCourse ?? '',
          nonKnowledgeGroup: {
            value: nonKnowledgeGroupCustomThreshold ?? undefined,
            type: nonKnowledgeGroupSelection ?? null,
          },
          minimumExamineePerQuestion: minExaminees ?? 0,
          knowledgeGroup: {
            value: knowledgeGroupCustomThreshold ?? undefined,
            type: knowledgeGroupSelection ?? null,
          },
          endedAt: new Date(
            endYear && endMonth
              ? new Date(Date.UTC(endYear, monthIndexMap[endMonth], 0, 16, 59, 59))
              : '',
          ),
        },
      },
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const signedUrl = data.reportExportQuestionItemAnalysisYearlyRecords.signedUrl;
        if (!signedUrl) {
          showSnackbar({
            message: 'ส่งออกข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
            variant: 'error',
          });
          onClose();
          return;
        }

        triggerFileDownload(signedUrl);
        showSnackbar({
          message: 'ส่งออกข้อมูลเรียบร้อยแล้ว',
          variant: 'success',
        });
        onClose();
      },
      onError: () => {
        showSnackbar({
          message: 'ส่งออกข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
          variant: 'error',
        });
        onClose();
      },
    });
  };

  const isExceedingMaximumExportCount = totalCount > MAXIMUM_EXPORT_COUNT;
  const disableExport = isExporting || isExceedingMaximumExportCount;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='xs'>
      <DialogTitle onClose={onClose} loading={isExporting}>
        ยืนยันการส่งออกข้อมูล
      </DialogTitle>
      <DialogContent dividers>
        <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(3) })}>
          {isExceedingMaximumExportCount ? (
            <Alert severity='error'>
              จำกัดการดาวน์โหลดสูงสุด {MAXIMUM_EXPORT_COUNT.toLocaleString('th-TH')} รายการต่อครั้ง
            </Alert>
          ) : null}
          <Text variant='body1Semibold'>สรุปข้อมูลที่ต้องการ</Text>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
              Date:
            </Text>
            <Text>
              {formattedStartDate} - {formattedEndDate}
            </Text>
          </Flex>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
              คลังข้อสอบ:
            </Text>
            <Text>{selectedQBankStructure?.name}</Text>
          </Flex>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
              สถานะ:
            </Text>
            <Text>
              {questionStatus.map((status) => QuestionStatusLabelMapping[status]).join(', ')}
            </Text>
          </Flex>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
              รวมทั้งหมด:
            </Text>
            <Text>{totalCount.toLocaleString('th-TH')} รายการ</Text>
          </Flex>
        </Flex>
      </DialogContent>
      <DialogActions>
        <Flex sx={(theme) => ({ gap: theme.spacing(2), justifyContent: 'flex-end' })}>
          <Button size='large' variant='outlined' color='secondary' onClick={onClose}>
            ยกเลิก
          </Button>
          <Button
            size='large'
            loading={isExporting}
            disabled={disableExport}
            onClick={handleExport}
          >
            ส่งออกข้อมูล
          </Button>
        </Flex>
      </DialogActions>
    </Dialog>
  );
}
